import { Fragment, useMemo } from "react";
import { Link } from "@tanstack/react-router";
import { GLOSSARY } from "@/data/glossary";

/**
 * Auto-links glossary terms inside story prose.
 *
 * Best-practice rules applied:
 *  - first occurrence of a term only (no repeated links for the same concept)
 *  - a hard cap per story so body copy never turns into a link farm
 *  - whole-word, case-insensitive matching, longest terms first
 *  - generic words are excluded so links always point somewhere useful
 *  - links carry a title attribute with the glossary definition
 */

const MAX_LINKS_PER_STORY = 4;

/** Words too generic to auto-link inside narrative prose. */
const EXCLUDED = new Set([
  "quote",
  "quotes",
  "quotation",
  "saying",
  "sayings",
  "citation",
  "attribution",
  "paraphrase",
  "context",
  "source",
  "truism",
  "mantra",
  "voice",
  "tone",
  "irony",
  "sic",
]);

type Term = { term: string; slug: string; short: string };

function linkableTerms(): Term[] {
  return GLOSSARY.filter((g) => !EXCLUDED.has(g.term.toLowerCase()) && g.term.length >= 5)
    .map((g) => ({ term: g.term, slug: g.slug, short: g.short }))
    .sort((a, b) => b.term.length - a.term.length);
}

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function StoryProse({ paragraphs }: { paragraphs: string[] }) {
  const rendered = useMemo(() => {
    const terms = linkableTerms();
    const used = new Set<string>();
    let links = 0;

    return paragraphs.map((paragraph) => {
      const nodes: Array<string | Term & { matched: string }> = [paragraph];

      for (const t of terms) {
        if (links >= MAX_LINKS_PER_STORY) break;
        if (used.has(t.slug)) continue;

        const re = new RegExp(`\\b${escapeRegExp(t.term)}\\b`, "i");
        for (let i = 0; i < nodes.length; i++) {
          const node = nodes[i];
          if (typeof node !== "string") continue;
          const m = re.exec(node);
          if (!m) continue;
          const before = node.slice(0, m.index);
          const after = node.slice(m.index + m[0].length);
          nodes.splice(i, 1, before, { ...t, matched: m[0] }, after);
          used.add(t.slug);
          links++;
          break;
        }
      }

      return nodes;
    });
  }, [paragraphs]);

  return (
    <>
      {rendered.map((nodes, i) => (
        <p key={i}>
          {nodes.map((node, j) =>
            typeof node === "string" ? (
              <Fragment key={j}>{node}</Fragment>
            ) : (
              <Link
                key={j}
                to="/glossary/$slug"
                params={{ slug: node.slug }}
                title={node.short}
                className="text-gold underline decoration-gold/40 underline-offset-4 hover:decoration-gold"
              >
                {node.matched}
              </Link>
            ),
          )}
        </p>
      ))}
    </>
  );
}
